/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import {
  downloadFile,
  accessAPI,
  ArchivoPreview,
  subirArchivosPesadosS3,
  Sidebar,
  Formulario,
  TablaFiltrable
} from "../../shared";
import { Boton } from "../../shared";
import { Column } from "primereact/column";
import { Menu } from "primereact/menu";
import { confirmDialog } from "primereact/confirmdialog";
import { Titulo } from "../../shared";
import { Toast } from "primereact/toast";
import { Card } from "primereact/card";
import { Tooltip } from "primereact/tooltip";

export default function ArchivosDisponibles({
  archivosOriginales,
  pasoId,
  modoLectura,
  procesoNombre,
  // Archivo que se agregó desde afuera (p. ej. movido desde un hallazgo): se
  // suma a la lista sin pisar lo que se subió o borró desde esta card
  archivoAgregado,
}) {
  const [archivos, setArchivos] = useState(archivosOriginales);

  useEffect(() => {
    if (!archivoAgregado) return;
    setArchivos((actual) =>
      actual.some((a) => a.id === archivoAgregado.id && !a.esDeProceso)
        ? actual
        : [...actual, archivoAgregado]
    );
  }, [archivoAgregado]);

  const [descargandoArchivoId, setDescargandoArchivoId] = useState(null);
  // "actividad" | "proceso" mientras se arma el zip de esa card
  const [descargandoZip, setDescargandoZip] = useState(null);
  const [archivoSeleccionado, setArchivoSeleccionado] = useState(null);
  const menuRef = useRef(null);

  // Sidebar de subida: el archivo se sube con una categoría elegida de las
  // configuradas por el organismo como disponibles para colaboradores
  const [sideBarSubir, setSideBarSubir] = useState(false);
  const [categorias, setCategorias] = useState([]);
  const [formLoaderSubir, setFormLoaderSubir] = useState(false);
  const [formKeySubir, setFormKeySubir] = useState(0);

  const toast = useRef(null);

  // Los archivos del proceso vienen marcados por la API con esDeProceso
  const archivosActividad = archivos.filter((a) => !a.esDeProceso);
  const archivosProceso = archivos.filter((a) => a.esDeProceso);

  // Carga las categorías de archivos disponibles para colaboradores
  useEffect(() => {
    if (modoLectura) return;
    accessAPI(
      "GET",
      "colab/archivo/categorias",
      null,
      (respuesta) => {
        setCategorias(respuesta);
      },
      () => {}
    );
  }, [modoLectura]);

  // Sube el archivo del formulario con su categoría (subida directa a S3 vía
  // presigned URL) y confirma contra la API
  function subirArchivo(contenidoCampos, archivo) {
    if (!archivo) {
      return;
    }
    setFormLoaderSubir(true);
    subirArchivosPesadosS3({
      presignEndpoint: "colab/archivo/presign",
      confirmarEndpoint: "colab/actividad/subirarchivo/confirmar",
      archivos: archivo,
      datosPresign: { ubicacion: "procesos" },
      datosConfirmar: {
        actividadPasoId: pasoId,
        categoria: contenidoCampos.categoria || null,
      },
    })
      .then((creados) => {
        setArchivos((actual) => [...actual, ...creados]);
        setFormLoaderSubir(false);
        setSideBarSubir(false);
        toast.current?.show({
          severity: "success",
          summary: "Subido",
          detail: "Archivo subido exitosamente",
          life: 3000,
        });
      })
      .catch((error) => {
        setFormLoaderSubir(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: error?.[0]?.msg || "Error al subir archivo",
          sticky: true,
        });
      });
  }

  // Función para descargar archivo
  function descargarArchivo(archivoId, nombreArchivo) {
    setDescargandoArchivoId(archivoId);
    downloadFile(
      `colab/actividad/descargaarchivopaso/${pasoId}/${archivoId}`,
      nombreArchivo,
      () => {
        setDescargandoArchivoId(null);
      },
      (respuesta) => {
        setDescargandoArchivoId(null);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0].msg || "Error al descargar el archivo",
          sticky: true,
        });
      }
    );
  }

  // Descarga todos los archivos de una card en un zip
  function descargarTodos(tipo) {
    setDescargandoZip(tipo);
    const nombreZip = procesoNombre
      ? `Archivos ${tipo} - ${procesoNombre}.zip`
      : `Archivos ${tipo}.zip`;
    downloadFile(
      `colab/actividad/descargazippaso/${pasoId}/${tipo}`,
      nombreZip,
      () => {
        setDescargandoZip(null);
      },
      (respuesta) => {
        setDescargandoZip(null);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al descargar los archivos",
          sticky: true,
        });
      }
    );
  }

  // Botón de descarga del zip, debajo del datatable de cada card
  function botonDescargarTodos(tipo, cantidad) {
    if (cantidad === 0) {
      return null;
    }
    return (
      <div css={{ display: "flex", justifyContent: "flex-end", marginTop: "1em" }}>
        <Boton
          texto
          loader={descargandoZip === tipo}
          callback={() => descargarTodos(tipo)}
        >
          Descargar todos
        </Boton>
      </div>
    );
  }

  // Función para eliminar archivo
  function eliminarArchivo(archivo) {
    accessAPI(
      "DELETE",
      `colab/actividad/eliminararchivopaso/${pasoId}/${archivo.id}`,
      null,
      () => {
        setArchivos((actual) =>
          actual.filter((a) => a.id !== archivo.id)
        );
        toast.current?.show({
          severity: "success",
          summary: "Éxito",
          detail: "Archivo eliminado correctamente",
          life: 3000,
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar archivo",
          sticky: true,
        });
      }
    );
  }

  // Items del menú contextual
  const getMenuItems = () => {
    if (!archivoSeleccionado) return [];
    return [
      {
        label: "Descargar",
        icon: "pi pi-download",
        command: () =>
          descargarArchivo(archivoSeleccionado.id, archivoSeleccionado.nombredescarga),
      },
      {
        label: "Eliminar",
        icon: "pi pi-trash",
        command: () => {
          confirmDialog({
            message: "¿Está seguro que desea eliminar este archivo?",
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Sí",
            rejectLabel: "No",
            accept: () => eliminarArchivo(archivoSeleccionado),
          });
        },
      },
    ];
  };

  function botonDescarga(archivo) {
    const descargando = descargandoArchivoId === archivo.id;
    return (
      <Boton
        callback={() => descargarArchivo(archivo.id, archivo.nombredescarga)}
        disabled={descargando}
        chico
        icono
        negro
      >
        <i className={descargando ? "pi pi-spin pi-spinner" : "pi pi-download"}></i>
      </Boton>
    );
  }

  function accionesArchivoActividad(archivo) {
    if (modoLectura) {
      return botonDescarga(archivo);
    }
    const descargando = descargandoArchivoId === archivo.id;
    return (
      <div css={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5em" }}>
        {descargando && <i className="pi pi-spin pi-spinner" />}
        <Boton
          callback={(e) => {
            setArchivoSeleccionado(archivo);
            menuRef.current.toggle(e);
          }}
          icono
          chico
          negro
        >
          <i className="pi pi-ellipsis-v"></i>
        </Boton>
      </div>
    );
  }

  // Una sola columna: la categoría como texto principal y el nombre del
  // archivo debajo, en gris. Sin categoría, solo el nombre.
  function columnaArchivo() {
    return (
      <Column
        field="nombredescarga"
        header="Archivo"
        body={(rowData) => (
          <ArchivoPreview
            nombre={rowData.nombredescarga}
            hash={rowData.hash}
            etiqueta={rowData.categoria || undefined}
            subtitulo={rowData.categoria ? rowData.nombredescarga : undefined}
          />
        )}
      />
    );
  }

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <Menu model={getMenuItems()} popup ref={menuRef} />

      {/* Sidebar de subida de archivo con categoría */}
      <Sidebar
        visible={sideBarSubir}
        onHide={() => setSideBarSubir(false)}
        position="right"
        className="w-full lg:w-30rem md:w-30rem"
      >
        <Titulo>Subir archivo</Titulo>
        <Formulario
          key={formKeySubir}
          config={{
            secciones: [
              {
                campos: [
                  {
                    nombre: "categoria",
                    etiqueta: "Categoría",
                    tipoitemformularioid: 5, // dropdown
                    opciones: categorias,
                    sinOpcionInicial: true,
                  },
                  {
                    nombre: "archivo",
                    etiqueta: "Archivo",
                    tipoitemformularioid: 10, // file
                    requerido: true,
                  },
                ],
              },
            ],
            botones: [
              {
                etiqueta: "Subir archivo",
                callback: subirArchivo,
                validar: true,
              },
            ],
          }}
          loader={formLoaderSubir}
        />
      </Sidebar>

      <Card>
        <Titulo
          botones={
            !modoLectura && (
              <Boton
                icono
                negro
                callback={() => {
                  setFormKeySubir((k) => k + 1);
                  setSideBarSubir(true);
                }}
              >
                <i className="pi pi-plus"></i>
              </Boton>
            )
          }
        >
          <i className="pi pi-folder-open" css={{ marginRight: "0.5em" }}></i>
          Archivos de la actividad
          <Tooltip target=".tooltip-archivos-actividad" position="top" />
          <i
            className="pi pi-question-circle tooltip-archivos-actividad"
            data-pr-tooltip="Estos son los archivos propios de la actividad, se deben evaluar en esta instancia"
            css={{ marginLeft: "0.5em", fontSize: "0.8em" }}
          ></i>
        </Titulo>
        <TablaFiltrable conFiltros={false}
          key={`archivos-actividad-${descargandoArchivoId}`}
          value={archivosActividad}
          emptyMessage="No hay archivos cargados"
          size="small"
        >
          {columnaArchivo()}
          <Column
            body={accionesArchivoActividad}
            style={{ width: "3em", textAlign: "center" }}
          />
        </TablaFiltrable>
        {botonDescargarTodos("actividad", archivosActividad.length)}
      </Card>
      <Card>
        <Titulo>
          <i className="pi pi-folder-open" css={{ marginRight: "0.5em" }}></i>
          Archivos del proceso
          <Tooltip target=".tooltip-archivos-proceso" position="top" />
          <i
            className="pi pi-question-circle tooltip-archivos-proceso"
            data-pr-tooltip="Estos son archivos correspondientes a una actividad anterior. Se incluyen ya que pueden ser relevantes para la actividad actual pero puede que no apliquen."
            css={{ marginLeft: "0.5em", fontSize: "0.8em" }}
          ></i>
        </Titulo>
        <TablaFiltrable conFiltros={false}
          key={`archivos-proceso-${descargandoArchivoId}`}
          value={archivosProceso}
          emptyMessage="No hay archivos del proceso disponibles"
          size="small"
        >
          {columnaArchivo()}
          <Column
            body={botonDescarga}
            style={{ width: "3em", textAlign: "center" }}
          />
        </TablaFiltrable>
        {botonDescargarTodos("proceso", archivosProceso.length)}
      </Card>
    </>
  );
}
